import { getValidate } from '@lub0v/santa-utils';
import * as yup from 'yup';

/**
 * Same shape the santa-utils Yup locale puts on every failure
 * (`validations.invalidEmail`, `validations.required`, …).
 */
export type FieldError = {
  transKey: string;
  field?: string;
  params?: Record<string, unknown>;
};

function asFieldError(value: unknown): FieldError | undefined {
  if (value && typeof value === 'object' && 'transKey' in value) {
    const error = value as FieldError;
    return {
      transKey: error.transKey || 'validations.invalid',
      field: error.field,
      params: error.params,
    };
  }
  return undefined;
}

function firstFieldError(err: { errors?: unknown[] }, preferField?: string): FieldError {
  const raw = err.errors ?? [];
  const parsed = raw.map(asFieldError).filter((item): item is FieldError => Boolean(item));
  const preferred = preferField ? parsed.find((item) => item.field === preferField) : undefined;
  return preferred ?? parsed[0] ?? { transKey: 'validations.invalid' };
}

/**
 * Run the `@validate` Yup schema for one field on a santa-utils model.
 * `siblingKeys` are extra model fields included in the shape so `.when(...)`
 * rules can see them — same as v1 `validateField(model, 'cashLimit', 'useCashLimit')`.
 */
export function validateFieldError(
  model: object,
  field: string,
  values: Record<string, unknown>,
  siblingKeys: string[] = [],
): FieldError | undefined {
  const schema: Record<string, yup.AnySchema> = {};
  for (const key of [field, ...siblingKeys]) {
    const fieldSchema = getValidate(model, key);
    if (fieldSchema) {
      schema[key] = fieldSchema;
    }
  }
  if (!schema[field]) {
    return { transKey: 'validations.invalid' };
  }

  try {
    yup
      .object()
      .shape(schema)
      .validateSync({ ...model, ...values }, { abortEarly: false });
    return undefined;
  } catch (err) {
    return firstFieldError(err as { errors?: unknown[] }, field);
  }
}
